<template>
  <div class="LKPublicationsDataCollectsForm">
    <LKPublicationsFormdataHeader
      :name="name"
      :loading="isLoadingSave || loading"
      :loading-closing="loadingClosing"
      :with-draft="withDraft"
      :intermediate="intermediate"
      :close="close"
      :errors="hasErrors || hasBackendValidationErrors"
      :submitted="submitted"
      :readonly="readonly"
      :expandable="showFormTab"
      :updated="dataUpdated"
      :use-excel="useExcel"
      :rev-date="revDate"
      :sheets="sheets"
      :active-tab="showScanTab ? 'scan' : showFormTab ? 'form' : 'excel'"
      @submit="onSubmit"
      @draft:save="onDraftSave"
      @draft:clear="onDraftClear"
      @excel:download="$emit('excel:download')"
      @excel:upload="$emit('excel:upload')"
      @tab:excel="showFormTab = false; showScanTab = false"
      @tab:form="onTabForm"
      @tab:scan="onScanClick"
      @table:expanded="onTableExpandClick"
      @close="onClose"
      @showErrors="onShowErrors"
    />

    <div v-if="showScanTab" style="padding: 2rem 3rem;">
      <div
        class="scan-instruction-banner"
        @click="openScanInstruction"
      >
        <v-icon color="primary" size="32">
          mdi-file-pdf-box
        </v-icon>
        <div>
          <div class="scan-instruction-banner__title">
            Инструкция по сканированию титульных листов ВПР
          </div>
          <div class="scan-instruction-banner__subtitle">
            Нажмите, чтобы открыть PDF
          </div>
        </div>
        <v-icon color="primary" style="margin-left: auto;">
          mdi-chevron-right
        </v-icon>
      </div>

      <v-btn
        color="primary"
        rounded
        outlined
        class="mb-2"
        style="margin-top: 1rem;"
        :loading="isUploadingLoading"
        @click="onScanFilesUpload"
        v-text="'Загрузить файлы'"
      />

      <input
        v-show="false"
        ref="scanInput"
        type="file"
        multiple
        accept=".zip,.pdf,.png,.jpg,.jpeg,.tif,.tiff,.bmp,application/zip,application/pdf,image/png,image/jpeg,image/tiff,image/bmp"
        @input="onScanFileUpload($event.target.files)"
      >

      <div v-if="scanningStatus" class="d-flex align-center" style="margin-top: 1rem;">
        <template v-if="scanningStatus === 'processing'">
          <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
          Вы загрузили файлы. Идет обработка
        </template>
        <template v-else-if="scanningStatus === 'done'">
          <v-icon color="success" class="mr-2">
            mdi-check-circle
          </v-icon>
          Загруженные файлы обработаны. Распознавание завершено
        </template>
      </div>

      <v-tabs v-model="scanTabIndex" class="mb-4" style="margin-top: 1rem;">
        <v-tab>Все результаты сканирования</v-tab>
        <v-tab>Статус обработки файлов</v-tab>
      </v-tabs>

      <v-tabs-items v-model="scanTabIndex">
        <!-- Таб 1: Все результаты сканирования -->
        <v-tab-item>
          <LKPublicationsFormdataOcrResultsTable
            v-if="formDataId && publicationId"
            ref="ocrResultsTable"
            :form-data-id="formDataId"
            :publication-id="publicationId"
            :all_cells="all_cells"
          />
        </v-tab-item>

        <!-- Таб 2: Задачи сканирования -->
        <v-tab-item eager>
          <LKPublicationsFormdataOcrTable
            v-if="formDataId && publicationId"
            ref="ocrTable"
            :form-data-id="formDataId"
            :publication-id="publicationId"
            :all_cells="all_cells"
            @status-change="scanningStatus = $event"
          />
        </v-tab-item>
      </v-tabs-items>
    </div>

    <div v-else-if="!showFormTab" style="padding: 1rem;">
      <template v-if="formDataId">
        <!-- Если существует formDataId, то шаблон генерируется, иначе скачивается пустой -->
        <div v-if="['pending', 'started'].includes(excelStatus)">
          Шаблон подготавливается. <br>
          <div v-if="excelStatus === 'pending'">
            Время ожидания составит до 5 минут. Вы можете закрыть страницу и вернуться позже.
          </div>
          <div v-else>
            Ваш файл скоро будет готов.
          </div>
        </div>
        <div v-else>
          <LKPublicationsFormdataDownloadTemplateLink
            v-if="excelStatus === 'success'"
            :hide-upload-hint="close || readonly"
            @download="onExcelDownload"
          />
          <div v-if="excelStatus === 'failed'">
            При формировании шаблона файла произошла ошибка. В случае повторения ошибки обратитесь в тех поддержку.
          </div>
          <div>
            <v-btn
              color="primary"
              rounded
              outlined
              class="mb-2"
              style="margin-top: 1rem;"
              :loading="isStatusLoading"
              @click="generateExcel"
              v-text="'Сформировать excel шаблон'"
            />
          </div>
        </div>
      </template>
      <LKPublicationsFormdataDownloadTemplateLink
        v-else
        :hide-upload-hint="close || readonly"
        @download="onExcelDownload"
      />
      <v-btn
        v-if="!close && !readonly"
        color="primary"
        rounded
        outlined
        class="mb-2"
        style="margin-top: 1rem;"
        :loading="isUploadingLoading"
        @click="onExcelUpload"
        v-text="'Загрузить из excel'"
      />

      <input v-show="false" ref="input" type="file" @input="onFileUpload($event.target.files)">
      <div v-if="hasErrors || hasBackendValidationErrors" style="margin-top: 1rem;">
        Загруженный файл имеет ошибки. Вы можете посмотреть введенные данные во вкладке "Форма".
      </div>
      <div v-else-if="fileUploaded" style="margin-top: 1rem;">
        Вы загрузили данные формы. Нажмите "Сдать отчёт" для отправки отчета.
        <br>
        Загружено данных (строк\столбцов):
        <ul>
          <li v-for="(stats, sheet) in rowStats" :key="sheet">
            {{ sheet }} : {{ stats }} строк
          </li>
        </ul>
      </div>
      <div v-if="hasErrors || hasBackendValidationErrors" style="margin-top: 1rem;">
        <v-row no-gutters>
          <v-col cols="12" class="mt-2">
            <span>Ошибки заполнения формы:</span>
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col cols="6">
            <ul v-if="!errors['отчёт']" style="padding-left: 24px;">
              <li v-for="(values, key) in textPreparedErrors" :key="key">
                {{ getSheetNameByKey(key) }}
                <ul style="padding-left: 24px;">
                  <li v-for="error in values" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </li>
            </ul>
          </v-col>

          <v-col cols="6">
            <ul style="padding-left: 24px;">
              <li v-for="(values, key) in textPreparedBackendErrors" :key="key">
                {{ getSheetNameByKey(key) }}
                <ul style="padding-left: 24px;">
                  <li v-for="error in values" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </li>
            </ul>
          </v-col>
        </v-row>

        <template v-if="errors['отчёт']">
          <v-row v-for="(error, index) in errors['отчёт']" :key="`error-${index}`" no-gutters>
            <v-col>
              <p class="error--text">
                {{ error }}
              </p>
            </v-col>
          </v-row>
        </template>
      </div>
    </div>
    <template v-else-if="activeSheet && showFormTab">
      <LKPublicationsFormdataTable
        :all_cells="all_cells"
        :sheet="activeSheet"
        :list="list"
        :errors="activeSheetErrors"
        :vertical-line-data="verticalLineData"
        :actions="actions"
        :readonly="readonly || close"
        :can-edit="canEdit"
        :expanded="isTableExpanded"
        :should-update="forceTableUpdate"
        :schedule-information="scheduleInformation"
        class="LKPublicationsDataCollectsForm__inner"
        @action="onAction"
        @table:updated="onTableUpdated"
        @table:collapse="onTableCollapseClick"
      />

      <v-tabs v-model="activeSheetIndex" dense show-arrows>
        <v-tab v-for="(item, index) in sheets" :key="index" dense>
          <template #default>
            <span class="LKPublicationsDataCollectsForm__tab-title">
              <span v-if="item.name.length <= 31" v-text="item.name" />
              <v-tooltip v-else top>
                <template #activator="{ on }">
                  <span class="LKPublicationsDataCollectsForm__tab-title--long" v-on="on" v-text="item.name" />
                </template>

                <slot>
                  <span v-text="item.name" />
                </slot>
              </v-tooltip>

              <BInfo
                v-if="item.hint"
                icon="mdi-message-alert-outline"
                icon-class="ml-1"
                :text="item.hint"
                size="1rem"
              />
            </span>
          </template>
        </v-tab>
        <v-tabs-slider />
      </v-tabs>
    </template>

    <div v-else class="LKPublicationsDataCollectsForm__empty" v-text="'Нет активных страниц'" />

    <LKPublicationsFormdataUploadModal
      v-model="showUploadModal"
      :message="uploadMessage"
      :progress="uploadProgress"
    />
  </div>
</template>

<script>
import { ObserveVisibility } from 'vue-observe-visibility'
import { TableActionsTypes, ADD, DELETE } from '@/helpers/enums/table-actions'
import { createModalRemove } from '@/helpers/createModalRemove'
import { createCellData, isNewable } from '../createSheetData'
import countBy from 'lodash/countBy'
import uniq from 'lodash/uniq'
import { isNaN, isEmpty } from 'lodash'

import BInfo from '@/components/common/BInfo.vue'
import LKPublicationsFormdataHeader from './LKPublicationsFormdataHeader.vue'
import LKPublicationsFormdataTable from './LKPublicationsFormdataTable.vue'
import LKPublicationsFormdataUploadModal from './LKPublicationsFormdataUploadModal.vue'
import LKPublicationsFormdataOcrTable from './LKPublicationsFormdataOcrTable.vue'
import LKPublicationsFormdataOcrResultsTable from './LKPublicationsFormdataOcrResultsTable.vue'
import { LkFormTemplateApi, LkFormdataApi } from '@/api/resources'
import { CollectorUploadApi } from '@/api/resources/Collector/CollectorUpload'
import { CollectorOcrApi } from '@/api/resources/Collector/CollectorOcrApi'
import { downloadLinkByGet, downloadBlob } from '@/helpers/downloadBlob'
import { getErrorMessages } from '@/helpers/getErrorMessages'
import {
  SheetCellDepsTypes,
  SheetCellTypeMethods,
  SheetCellTypes,
  DEPENDENCE_EMPTY_VALUE,
} from '@/typings/api/FormTemplate'
import { formsHandbookMapper as StoreMapper } from '@/views/FormsHandbook/store/cache'
import { evaluateFormula } from '@/views/LKPublications/formdata/components/utils'
import LKPublicationsFormdataDownloadTemplateLink
  from '@/views/LKPublications/formdata/components/LKPublicationsFormdataDownloadTemplateLink.vue'
import { createLKPublicationsFormdataErrorsModal } from '@/views/LKPublications/formdata/utils'
import { LkScheduleApi } from '@/api/resources/LkScheduleApi'

const SAVE_DRAFT_TIMEOUT = 1_000 * 60 * 2 // 2 minutes

export default {
  name: 'LKPublicationsFormdataForm',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  directives: {
    ObserveVisibility,
  },
  components: {
    LKPublicationsFormdataDownloadTemplateLink,
    BInfo,
    LKPublicationsFormdataHeader,
    LKPublicationsFormdataTable,
    LKPublicationsFormdataUploadModal,
    LKPublicationsFormdataOcrTable,
    LKPublicationsFormdataOcrResultsTable,
  },
  inheritAttrs: false,
  props: {
    loading: Boolean,
    loadingClosing: Boolean,
    withDraft: Boolean,
    close: Boolean,
    submitted: Boolean,
    readonly: Boolean,
    canEdit: Boolean,
    intermediate: Boolean,
    dataCollectionEmpty: Boolean,
    formTemplateId: Number,
    useExcel: Boolean,
    dataCollectionId: {
      type: [String, Number],
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    sheets: {
      type: Array,
      required: true,
    },
    dataCollection: {
      type: Array,
      required: true,
      validator: (prop) => prop.length > 0,
    },
    dataPrefilled: {
      type: Array,
    },
    formDataId: {
      type: [String, Number],
    },
    publicationId: {
      type: Number,
      default: null,
    },
    revDate: String,
    procedureDigitCode: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    activeSheetIndex: 0,
    isLoadingSave: false,
    showFormTab: true,
    showScanTab: false,
    scanTabIndex: 0,
    fileUploaded: false,
    scanningStatus: null,
    isUploadingLoading: false,
    isDownloading: false,
    isStatusLoading: false,
    statusLoadingTime: 2_000,
    excelStatus: null,
    textPreparedErrors: {},
    errors: {},
    rowStats: {},
    textPreparedBackendErrors: {},
    backendErrors: [],
    forceTableUpdate: false,
    isCorrect: false,
    isTableExpanded: false,
    dataUpdated: false,
    showUploadModal: false,
    uploadMessage: 'Файл загружается',
    uploadProgress: 0,
    scheduleInformation: {
      data: [],
    },
  }),
  computed: {
    scannableCellsCount() {
      let count = 0
      this.sheets.forEach((sheet) => {
        if (sheet.cells) {
          sheet.cells.forEach((cell) => {
            if (cell.is_scannable === true) {
              count += 1
            }
          })
        }
      })
      return count
    },
    hasErrors() {
      // eslint-disable-next-line no-empty
      if (this.forceTableUpdate) { } // добавлена зависимость, чтобы форсануть перерасчёт после изменения элементов списка
      return Object.keys(this.errors).length > 0
        || (!this.fileUploaded && this.dataCollection[this.activeSheetIndex].length === 0)
        || this.dataCollection[this.activeSheetIndex].length === 0
    },
    hasSchedule() {
      return this.sheets.some((sheet) => sheet.cells.some((cell) => cell.type === SheetCellTypes.schedule))
    },
    hasBackendValidationErrors() {
      return this.backendErrors.length > 0
    },
    activeSheet() {
      return this.sheets[this.activeSheetIndex]
    },
    activeSheetErrors() {
      return this.errors[this.activeSheetIndex]
    },
    list() {
      return this.dataCollection[this.activeSheetIndex]
    },
    actions() {
      if (this.close || this.readonly) return []
      const newable = isNewable(this.activeSheet.cells)
      return newable
        ? [
          {
            ...ADD,
            getFilter: (data, index, list) => index === (list.length - 1) || list.length === 1,
          },
          {
            ...DELETE,
            getFilter: (data) => !data.$readonly,
          },
        ]
        : []
    },
    all_cells_by_uuid() {
      const { sheets, dataCollection, dataPrefilled } = this
      const listMap = {}
      sheets
        .map((sheet, index) => {
          // eslint-disable-next-line no-param-reassign
          sheet.$index = index
          // eslint-disable-next-line no-param-reassign
          sheet.$data = dataCollection[index]
          if (dataPrefilled?.[index]?.length) {
            // eslint-disable-next-line no-param-reassign
            sheet.$prefilled_data = dataPrefilled[index]
          }
          return sheet.cells.map((cell, $index) => {
            // eslint-disable-next-line no-param-reassign
            cell.$sheet = sheet
            // eslint-disable-next-line no-param-reassign
            cell.$index = $index
            return cell
          })
        })
        .flat(1)
        .forEach((cell) => {
          if (!listMap[cell.uuid]) listMap[cell.uuid] = cell
          else listMap[cell.uuid] = { ...listMap[cell.uuid], ...cell }

          if (cell.formula) {
            [...cell.formula.matchAll(/\[#\$_(.+?)#\]/g)].forEach(([, uuid]) => {
              if (!listMap[uuid]) listMap[uuid] = {}
              if (!listMap[uuid].usedInCellsWithFormula) listMap[uuid].usedInCellsWithFormula = []
              if (cell.uuid !== uuid && !listMap[uuid].usedInCellsWithFormula.includes(cell.uuid)) {
                listMap[uuid].usedInCellsWithFormula.push(cell.uuid)
              }
            })
          }
        })

      return listMap
    },
    all_cells() {
      return Object.values(this.all_cells_by_uuid)
    },
    /**
     * Данные с вертикальных страниц
     */
    verticalLineData() {
      const data = {}
      this.sheets.filter((sheet) => !!sheet.vertical).forEach((sheet) => {
        [data[sheet.uuid]] = sheet.$data
      })
      return data
    },
    isEmpty() {
      if (this.dataCollectionEmpty) return true
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.dataCollection.length; i++) {
        const sheetData = this.dataCollection[i]
        if (sheetData.length === 0) return true
        if (sheetData.length > 1) return false

        const initRow = Object.values(sheetData[0])
        return initRow.length === 0 || initRow.every((value) => !value)
      }
      return true
    },
    uploadedRows() {
      let notification = 'Форма успешно загружена'
      Object.keys(this.rowStats).forEach((sheet) => {
        notification += `<br> ${sheet} : ${this.rowStats[sheet]} строк`
      })
      return notification
    },
  },
  watch: {
    dataCollection: {
      handler() {
        this.forceTableUpdate = true
      },
    },
    scanTabIndex: {
      handler(newVal) {
        // Обновляем данные при переключении вкладок в режиме сканирования
        if (this.showScanTab) {
          this.$nextTick(() => {
            if (newVal === 0) {
              // Таб "Результаты сканирования" - обновляем LKPublicationsFormdataOcrResultsTable
              if (this.$refs.ocrResultsTable) {
                this.$refs.ocrResultsTable.loadData()
              }
              // Сбрасываем состояние просмотра результатов в ocrTable
              if (this.$refs.ocrTable) {
                this.$refs.ocrTable.resetView()
              }
            } else if (newVal === 1) {
              // Таб "Задачи сканирования" - обновляем LKPublicationsFormdataOcrTable
              if (this.$refs.ocrTable) {
                this.$refs.ocrTable.resetView()
                this.$refs.ocrTable.loadData()
              }
            }
          })
        }
      },
    },
    //   'activeSheet.vertical': {
    //     immediate: true,
    //     handler(value) {
    //       if (value) {
    //         this.intervalId = setInterval(
    //           () => this.$emit('draft:save'),
    //           SAVE_DRAFT_TIMEOUT,
    //         )
    //       } else {
    //         clearInterval(this.intervalId)
    //       }
    //     },
    //   },
    hasSchedule: {
      async handler(v) {
        if (v) {
          if (!this.scheduleInformation.data.length) {
            const api = new LkScheduleApi()
            try {
              this.scheduleInformation.data = await api.getForDates(this.publicationId, this.dataCollectionId)
            } catch (e) {
              this.$notice.errors(e)
            }
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.intervalId = setInterval(
      () => this.onDraftSave(),
      SAVE_DRAFT_TIMEOUT,
    )
    setTimeout(() => {
      if (!this.isEmpty) {
        this.validate()
      }
    }, 20)
    this.initStatus()
    this.loadHandbooks()
  },
  beforeDestroy() {
    this.onTableCollapseClick()
    clearInterval(this.intervalId)
    clearTimeout(this.checkStatusTimeout)
  },
  methods: {
    onClose() {
      this.$emit('close', { skipSave: this.showScanTab })
    },
    onTabForm() {
      this.showFormTab = true
      this.showScanTab = false
      if (!this.intervalId) {
        this.intervalId = setInterval(
          () => this.onDraftSave(),
          SAVE_DRAFT_TIMEOUT,
        )
      }
      this.$emit('reload')
    },
    getSheetNameByKey(key) {
      return this.sheets[key]?.name || key
    },
    async onAction(action, data, meta) {
      this.dataUpdated = true
      switch (action) {
        case TableActionsTypes.add: {
          if (!await this.isScheduleCorrect()) {
            return
          }
          const cell = createCellData(this.activeSheet.cells)
          this.list.push(cell)
          this.forceTableUpdate = true
          break
        }

        case TableActionsTypes.delete: {
          const propsData = { titleText: 'Удалить строку' }
          const modal = await createModalRemove(this, propsData)
          const result = await modal.open()
          if (!result) return

          const index = this.list.indexOf(data)
          this.list.splice(index, 1)

          if (!this.list.length) {
            const cell = createCellData(this.activeSheet.cells)
            this.list.push(cell)
          }
          this.forceTableUpdate = true
          break
        }

        case TableActionsTypes.edit: {
          this.list[meta.index] = data
          await this.validateSheet(this.activeSheet, this.list, meta.index)
          break
        }

        default:
      }

      this.checkFormulaDependencies()
    },
    async onDraftClear() {
      await this.reset()
      this.$emit('draft:clear')
    },
    async onDraftSave(showNotice = true) {
      if (!await this.isScheduleCorrect()) {
        return
      }
      if (this.dataUpdated) {
        this.$emit('draft:save')
        this.dataUpdated = false
        if (showNotice) {
          this.$notice.create({
            text: 'Черновик формы сохранён',
            killer: true,
          })
        }
      }
    },
    async reset() {
      this.errors = {}
      this.textPreparedErrors = {}
      this.backendErrors = []
      this.textPreparedBackendErrors = {}

      await this.$validator.reset()
    },
    async onSubmit() {
      this.isLoadingSave = true
      if (!await this.isScheduleCorrect()) {
        this.isLoadingSave = false
        return
      }
      await this.$nextTick()
      await new Promise((resolve) => setTimeout(resolve, 300))

      let valid = true
      const { activeSheetIndex } = this

      await this.reset()

      let sheetIndexs = Array.from(this.sheets).map((_, i) => i)
      sheetIndexs.splice(activeSheetIndex, 1)
      sheetIndexs = [this.activeSheetIndex, ...sheetIndexs]

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const index of sheetIndexs) {
        this.activeSheetIndex = index
        // eslint-disable-next-line no-await-in-loop
        await this.$nextTick()
        // eslint-disable-next-line no-await-in-loop
        valid = await this.validate()
        if (!valid) break
      }

      this.isLoadingSave = false
      if (!valid) {
        await this.onShowErrors()
        return
      }
      this.activeSheetIndex = activeSheetIndex

      await this.reset()

      this.$emit('submit', this.dataCollection)
    },
    async initStatus() {
      await this.getStatus()
      this.periodicalCheckStatus()
    },
    periodicalCheckStatus() {
      if (['pending', 'started'].includes(this.excelStatus)) {
        this.checkStatusTimeout = setTimeout(async () => {
          await this.getStatus()
          this.statusLoadingTime = this.statusLoadingTime > 60_0000 ? 60_000 : this.statusLoadingTime * 2
          this.periodicalCheckStatus()
        }, this.statusLoadingTime)
      }
    },
    async generateExcel() {
      const api = new LkFormTemplateApi()
      this.isStatusLoading = true
      try {
        const { file_status } = await api.generate(this.formTemplateId, this.formDataId)
        this.excelStatus = file_status
        this.periodicalCheckStatus()
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isStatusLoading = false
    },
    async getStatus() {
      if (!this.formDataId) return
      const api = new LkFormTemplateApi()
      this.isStatusLoading = true
      try {
        const { file_status } = await api.getStatus(this.formTemplateId, this.formDataId)
        this.excelStatus = file_status
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isStatusLoading = false
    },
    async onExcelDownload() {
      const api = new LkFormTemplateApi()
      const { formTemplateId, formDataId, name } = this
      let link
      if (formDataId) {
        link = api.download(formTemplateId, formDataId)
      } else {
        link = api.getTemplate(formTemplateId)
      }
      this.isDownloading = true
      try {
        await downloadLinkByGet(link, `${name}.xlsx`)
      } catch (e) {
        this.$notice.errors(e)
      }
      setTimeout(() => {
        this.isDownloading = false
      }, 1500)
    },
    onExcelUpload() {
      this.$refs.input.click()
    },
    onScanFilesUpload() {
      this.$refs.scanInput.click()
    },
    async openScanInstruction() {
      try {
        const blob = await new CollectorOcrApi().getInstruction()
        downloadBlob(blob, null, { isNewTab: true })
      } catch {
        this.$notice.create({ type: 'error', text: 'Не удалось открыть инструкцию' })
      }
    },
    async onScanFileUpload(files) {
      if (!files || files.length === 0) {
        return
      }

      const allowedTypes = ['application/zip', 'application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/tiff', 'image/tif', 'image/bmp']
      const allowedExtensions = ['.zip', '.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.bmp']

      // Проверка типов файлов
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
        const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)

        if (!isValidType) {
          this.$notice.create({
            type: 'error',
            text: `Файл "${file.name}" имеет недопустимый формат. Разрешены: ZIP, PDF, PNG, JPG, JPEG, TIF, TIFF, BMP`,
          })
          this.$refs.scanInput.value = null
          return
        }
      }

      const MAX_TOTAL_SIZE = 1024 * 1024 * 1024 // 1 ГБ
      const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0)
      if (totalSize > MAX_TOTAL_SIZE) {
        this.$notice.create({
          type: 'error',
          text: `Суммарный размер файлов превышает 1 ГБ (выбрано: ${(totalSize / 1024 / 1024).toFixed(1)} МБ). Уменьшите количество файлов.`,
        })
        this.$refs.scanInput.value = null
        return
      }

      const api = new CollectorUploadApi()
      const formDataSize = this.scannableCellsCount
      const {
        formDataId, publicationId: lkPublicationId, dataCollectionId, formTemplateId, procedureDigitCode,
      } = this

      if (!formDataId || !lkPublicationId) {
        this.$notice.create({
          type: 'error',
          text: 'Отсутствуют необходимые данные для загрузки файлов',
        })
        return
      }

      this.showUploadModal = true
      this.uploadMessage = `Загрузка файлов (0 из ${files.length})`
      this.uploadProgress = 0
      this.isUploadingLoading = true

      try {
        let successCount = 0
        const errors = []
        const warnings = []

        for (let i = 0; i < files.length; i += 1) {
          const file = files[i]
          this.uploadMessage = `Загрузка файла "${file.name}" (${i + 1} из ${files.length})`
          this.uploadProgress = Math.round(((i + 1) / files.length) * 100)

          try {
            // eslint-disable-next-line no-await-in-loop
            const response = await api.uploadOCRFile(
              file, formDataId, lkPublicationId, formDataSize,
              dataCollectionId, formTemplateId, procedureDigitCode,
            )
            successCount += 1
            // Проверяем наличие warning_message в ответе
            if (response?.data?.warning) {
              warnings.push(`${file.name}: ${response.data.warning}`)
            }
          } catch (error) {
            const errorMessages = getErrorMessages(error, {})
            const errorText = errorMessages.join('; ')
            errors.push(`${file.name}: ${errorText || 'Ошибка загрузки'}`)
          }
        }

        this.showUploadModal = false

        if (successCount === files.length) {
          this.$notice.create({
            type: 'success',
            text: `Успешно загружено файлов: ${successCount}`,
          })
          this.fileUploaded = true
          // Отображаем warning сообщения если они есть
          if (warnings.length > 0) {
            this.$notice.create({
              type: 'warning',
              text: `Предупреждения при загрузке: ${warnings.join('; ')}`,
            })
          }
          // Обновляем таблицу OCR после загрузки
          if (this.$refs.ocrTable) {
            this.$refs.ocrTable.loadData()
          }
        } else if (successCount > 0) {
          this.$notice.create({
            type: 'warning',
            text: `Загружено файлов: ${successCount} из ${files.length}. Ошибки: ${errors.join('; ')}`,
          })
          this.fileUploaded = true
          // Отображаем warning сообщения если они есть
          if (warnings.length > 0) {
            this.$notice.create({
              type: 'warning',
              text: `Предупреждения при загрузке: ${warnings.join('; ')}`,
            })
          }
        } else {
          this.$notice.create({
            type: 'error',
            text: `Не удалось загрузить файлы. Ошибки: ${errors.join('; ')}`,
          })
        }
      } catch (error) {
        this.showUploadModal = false
        this.$notice.errors(error)
      } finally {
        this.isUploadingLoading = false
        this.$refs.scanInput.value = null
      }
    },
    ...StoreMapper.mapActions(['getOneById']),
    async loadHandbooks() {
      const ids = []
      this.all_cells.forEach((cell) => {
        if (cell.type === SheetCellTypes.list_handbook && cell.list_handbook?.id) {
          ids.push(cell.list_handbook.id)
        }
      })
      const uniqueIds = uniq(ids)
      await Promise.all(uniqueIds.map((id) => this.getOneById(id)))
    },
    async onFileUpload(files) {
      const api = new LkFormTemplateApi()
      const [file] = files

      this.fileUploaded = false
      this.forceTableUpdate = false
      this.isUploadingLoading = true

      await this.reset()

      try {
        const data = await api.uploadTemplate(this.formTemplateId, file)
        // const updatedData = [data[0].slice(0, 2)]
        const updatedData = data.data
        this.rowStats = data.row_stats
        await this.validate(updatedData)
        this.$emit('input', updatedData)
        this.fileUploaded = true
        this.$notice.create({
          container: '.form-status',
          layout: 'topRight',
          type: 'success',
          timeout: 6000,
          text: this.uploadedRows,
        })
        this.$emit('draft:save', { force: true })
      } catch (e) {
        if (e.errors) {
          try {
            this.parseTemplateErrors(e.errors)
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err)
          }
        } else {
          this.$notice.errors(e)
        }
      }
      this.$refs.input.value = null
      this.isUploadingLoading = false
      this.forceTableUpdate = true
    },

    parseTemplateErrors(errors) {
      const textErrors = errors.map((err) => {
        if (typeof err === 'string') {
          return err
        }
        if (err.type === 'incorrect_type') {
          return `Лист "${err.sheet}" - ${err.value} - Неверное имя колонки. Ожидаемое "${err.awaited}"`
        }
        if (err.type === 'incorrect_list_sheets_order') {
          return 'Порядок листов не совпадает с шаблоном'
        }
        if (err.type === 'incorrect_list_sheets_count') {
          return 'Количество листов не совпадает с шаблоном'
        }
        if (err.type === 'incorrect_header_count') {
          return `Лист "${err.sheet}" - Количество заголовков не совпадает с шаблоном`
        }
        if (err.type === 'incorrect_header_order') {
          return `Лист "${err.sheet}" - Порядок заголовков не совпадает с шаблоном`
        }
        if (err.type === 'incorrect_header') {
          return `Лист "${err.sheet}" - Неверное количество столбцов`
        }
        return `Лист "${err.value}" не найден в списке доступных для заполнения`
      })
      this.errors = { отчёт: textErrors }
    },
    parseErrorsArr(errors) {
      const errorsMap = {}
      // eslint-disable-next-line consistent-return
      errors.forEach((error) => {
        if (!error.includes('|')) {
          const key = 'отчёт'
          if (!errorsMap[key]) errorsMap[key] = []
          errorsMap[key].push(error)
          return
        }
        const [list, cell, text] = error.split('|')
        const [row, col] = cell.split(':')
        const textTrimmed = text.trim()
        if (!errorsMap[list]) errorsMap[list] = {}
        if (!errorsMap[list][textTrimmed]) errorsMap[list][textTrimmed] = {}
        if (!errorsMap[list][textTrimmed][row]) errorsMap[list][textTrimmed][row] = []
        errorsMap[list][textTrimmed][row].push(col)
      })

      return errorsMap
    },
    parseErrorsArrForListView(errors) {
      const errorsMap = {}
      // eslint-disable-next-line consistent-return
      errors.forEach((error) => {
        if (!error.includes('|')) {
          const key = 'отчёт'
          if (!errorsMap[key]) errorsMap[key] = []
          errorsMap[key].push(error)
          return
        }
        const [list, cell, text] = error.split('|')
        const [row, col] = cell.split(':')
        const hasCoords = !isNaN(row) && !isNaN(col) && row >= 0 && col >= 0

        if (!errorsMap[list]) errorsMap[list] = []
        if (hasCoords) {
          let numberCol = parseInt(col, 10)
          if (this.activeSheet.vertical) {
            numberCol += 1
          }
          const colFirst = Math.trunc(numberCol / 26) > 0 ? (Math.trunc(numberCol / 25) + 9).toString(36).toUpperCase() : ''
          const colSecond = (Math.trunc(numberCol % 26) + 10).toString(36).toUpperCase()

          if (this.activeSheet.vertical) {
            errorsMap[list].push(`${Number(row) + 1}:${colFirst}${colSecond} - ${text}`)
          } else {
            errorsMap[list].push(`${Number(row) + 2}:${colFirst}${colSecond} - ${text}`)
          }
        } else {
          errorsMap[list].push(text)
        }
      })
      return errorsMap
    },
    async validate(data) {
      await this.reset()
      const errors = await this.validateForm(data || this.dataCollection)
      this.errors = this.parseErrorsArr(errors)
      this.textPreparedErrors = this.parseErrorsArrForListView(errors)
      this.isCorrect = !errors.length
      return !errors.length
    },
    async validateUsingBackendApi(errors = null) {
      if (Array.isArray(errors) && errors.length > 0) {
        this.backendErrors = [].concat(errors)
      } else {
        const api = new LkFormdataApi()
        try {
          const data = await api.validate(this.formDataId) // null on success
          this.backendErrors = data?.errors || []
        } catch (e) {
          this.backendErrors = Array.isArray(e.errors) ? e.errors : []
        }
      }

      if (Array.isArray(this.backendErrors)) {
        let backendErrors = []
        this.backendErrors.forEach((e) => {
          if (Array.isArray(e)) {
            backendErrors = backendErrors.concat(e.map((el) => `${el.sheet_idx}|${el.row_idx}:${el.col_idx}| ${el.error}`))
          } else {
            backendErrors = backendErrors.concat(e)
          }
        })

        this.textPreparedBackendErrors = this.parseErrorsArrForListView(backendErrors)
      }
    },
    getColValues(sheet, sheetData) {
      const colValues = {}
      sheet.cells.forEach(($cell, index) => {
        if ($cell.unique) {
          colValues[index] = countBy(sheetData, index)
        }
      })

      return colValues
    },
    async validateForm(data) {
      if (!data || data.length > this.sheets.length) {
        return ['Количество листов не совпадает с шаблоном']
      }

      const errors = []
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.sheets.length; i++) {
        const sheet = this.sheets[i]
        const sheetData = data[i]
        const colValues = this.getColValues(sheet, sheetData)

        if (sheet.required && isEmpty(sheetData)) {
          errors.push(`${i}|-1:-1|Лист должен содержать хотя бы одну строку`)
        }

        // eslint-disable-next-line no-plusplus
        for (let rowIndex = 0; rowIndex < sheetData.length; rowIndex++) {
          const row = sheetData[rowIndex]
          const rowKeys = Object.keys(row).filter((key) => key !== '$readonly' && key !== 'image')
          if (rowKeys.length > sheet.cells.length) {
            errors.push(`В листе "${sheet.name}" количество столбцов не совпадает с шаблоном`)
            break
          }
          // eslint-disable-next-line no-plusplus
          for (let j = 0; j < sheet.cells.length; j++) {
            const cell = sheet.cells[j]
            const value = row[cell.$index || j]

            // eslint-disable-next-line no-await-in-loop
            const validationError = await this.validateCell(sheet.cells, row, cell, value, colValues[j])
            if (validationError) {
              if (this.activeSheet.vertical) {
                errors.push(`${i}|${j}:${rowIndex}| ${validationError}`)
              } else {
                errors.push(`${i}|${rowIndex}:${j}| ${validationError}`)
              }
            }
          }
        }
      }
      return errors
    },
    async validateSheet(sheet, sheetData, rowIndex = null) {
      const errors = []
      const i = this.sheets.indexOf(sheet)
      if (i === -1) return
      const colValues = this.getColValues(sheet, sheetData)

      async function validateRow(rowIndexValue) {
        const row = sheetData[rowIndexValue]
        const rowKeys = Object.keys(row).filter((key) => key !== '$readonly' && key !== 'image')
        if (rowKeys.length > sheet.cells.length) {
          errors.push(`В листе "${sheet.name}" количество столбцов не совпадает с шаблоном`)
          return
        }
        // eslint-disable-next-line no-plusplus
        for (let j = 0; j < sheet.cells.length; j++) {
          const cell = sheet.cells[j]
          const value = row[cell.$index]

          // eslint-disable-next-line no-await-in-loop
          const validationError = await this.validateCell(sheet.cells, row, cell, value, colValues[j])
          if (validationError) {
            errors.push(`${i}|${rowIndexValue}:${j}| ${validationError}`)
          }
        }
      }

      if (rowIndex === null) {
        // eslint-disable-next-line no-plusplus
        for (let index = 0; index < sheetData.length; index++) {
          // eslint-disable-next-line no-await-in-loop
          await validateRow.call(this, index)
        }
      } else {
        await validateRow.call(this, rowIndex)
      }

      let sheetErrors = this.errors[i] || {}
      if (errors.length) {
        const errorsMap = this.parseErrorsArr(errors)

        if (rowIndex === null) sheetErrors = errorsMap[i]
        else if (typeof errorsMap[i] === 'object') {
          const affectedErrors = Object.keys(errorsMap[i])
          affectedErrors.forEach((text) => {
            if (!sheetErrors[text]) {
              sheetErrors[text] = {}
            }
            sheetErrors[text][rowIndex] = errorsMap[i][text][rowIndex]
          })
          Object.keys(sheetErrors).forEach((text) => {
            if (affectedErrors.includes(text)) return
            delete sheetErrors[text][rowIndex]
          })
        }
      } else if (rowIndex === null) {
        delete this.errors[i]
      } else if (typeof sheetErrors === 'object') {
        Object.keys(sheetErrors).forEach((text) => {
          delete sheetErrors[text][rowIndex]
          if (Object.keys(sheetErrors[text]).length === 0) delete sheetErrors[text]
        })
      }

      if (typeof sheetErrors === 'object' && Object.keys(sheetErrors).length) {
        this.$set(this.errors, i, sheetErrors) // Update table error icon.
      } else {
        delete this.errors[i]
      }
      this.forceTableUpdate = true
    },
    async validateCell(row_cells, row_values, cell, value, colValues = []) {
      const { type, type_method } = cell
      const valueIsEmpty = value === null || value === undefined || value === ''
      if (valueIsEmpty) {
        if (type === SheetCellTypes.formula) return null
        if (!cell.required) return null
        if (!cell.dependence.type) return 'Ячейка обязательна для заполнения'
      }

      if (type_method === SheetCellTypeMethods.dep_list) {
        const refCellIndex = row_cells.findIndex(({ uuid }) => uuid === cell.dep_list_cell_uuid)
        const refCellValue = row_values[refCellIndex]?.toString()
        const availableValuesList = cell.dep_list_values[refCellValue] || []

        let isValueInList = true
        if (cell.dependence.type) {
          const validateResults = cell.dependence.list.map((dependence) => {
            const refDepCellIndex = row_cells.findIndex(({ uuid }) => uuid === dependence.cell_uuid)
            const refValue = row_values[refDepCellIndex]
            if (refValue === null || refValue === undefined || refValue === '') {
              return dependence.list.includes(DEPENDENCE_EMPTY_VALUE)
            }
            return dependence.list.includes(refValue.toString())
          })
          isValueInList = cell.dependence.type === SheetCellDepsTypes.some
            ? validateResults.some(Boolean) : validateResults.every(Boolean)
        }

        if (availableValuesList.length === 0) {
          return value ? 'Ячейка не может быть заполнена' : null
        }
        if ((value === null || value === undefined || !availableValuesList.includes(value.toString()))
            && isValueInList) {
          return `Значение не находится в списке: ${availableValuesList}`
        }
        return null
      }
      if (cell.dependence.type) {
        const validateResults = cell.dependence.list.map((dependence) => {
          const refCellIndex = row_cells.findIndex(({ uuid }) => uuid === dependence.cell_uuid)
          const refValue = row_values[refCellIndex]
          if (refValue === null || refValue === undefined || refValue === '') {
            return dependence.list.includes(DEPENDENCE_EMPTY_VALUE)
          }
          return dependence.list.includes(refValue.toString())
        })
        const isValueInList = cell.dependence.type === SheetCellDepsTypes.some
          ? validateResults.some(Boolean) : validateResults.every(Boolean)
        if (isValueInList && valueIsEmpty && cell.required) {
          return 'Ячейка обязательна для заполнения'
        } if (!isValueInList) {
          return valueIsEmpty ? null : 'Ячейка не может быть заполнена'
        }
      }

      if (valueIsEmpty) return null

      if (cell.unique) {
        if (type === SheetCellTypes.number) {
          if (typeof value === 'string') {
            if (value.includes(',') || value.includes('.')) {
              const v1 = value.replace(',', '.')
              const v2 = value.replace('.', ',')
              if (colValues[v1] >= 1 && colValues[v2] >= 1) return 'Ячейка имеет неуникальное значение'
            }
          }
        }
        if (colValues[value] > 1) return 'Ячейка имеет неуникальное значение'
      }

      if (type_method === SheetCellTypeMethods.list || type === SheetCellTypes.drop_list) {
        if (!cell.list.includes(value.toString())) {
          return `Значение не находится в списке: ${cell.list.length < 10 ? cell.list.join(', ') : ''}`
        }
      }
      if (type === SheetCellTypes.list_handbook) {
        const handbookValues = await this.getHandbookValues(cell.list_handbook.id)
        if (!handbookValues.includes(value.toString())) {
          return `Значение не находится в списке: ${handbookValues.length < 10 ? handbookValues.join(', ') : ''}`
        }
      }
      if (type_method === SheetCellTypeMethods.number_interval) {
        if (value > cell.number_to || value < cell.number_from) {
          return `Значение не находится в промежутке ${cell.number_from}-${cell.number_to}`
        }
      }
      if (type === SheetCellTypes.number && type_method !== SheetCellTypeMethods.list) {
        let v = value
        if (typeof value === 'string') {
          v = value.replace(',', '.')
          const regex = /^\d+(\.\d+)?$/
          if (!regex.test(v) || /^0\d+/.test(v) || (v.endsWith('0') && v.includes('.'))) {
            return 'Значение не является числом'
          }
        }

        if (!Number(v) && value !== 0 && value !== '0') {
          return 'Значение не является числом'
        }
      }

      if (type === SheetCellTypes.string && cell.string_max && cell.string_min) {
        if (value.length > cell.string_max) return `Длина строки больше ${cell.string_max} символов`
        if (value.length < cell.string_min) return `Длина строки меньше ${cell.string_min} символов`
      }
      return null
    },
    ...StoreMapper.mapActions(['getOneById']),
    async getHandbookValues(handbook_id) {
      const { handbook_values } = await this.getOneById(handbook_id)

      return handbook_values
    },
    onTableExpandClick() {
      this.isTableExpanded = true
      this.forceTableUpdate = true
      document.getElementsByTagName('html')[0].style.setProperty('overflow', 'hidden')
    },
    onTableUpdated() {
      this.dataUpdated = true
      this.forceTableUpdate = false
      // this.$emit('draft:save')
      this.validate()
    },
    onTableCollapseClick() {
      this.isTableExpanded = false
      this.forceTableUpdate = true
      document.getElementsByTagName('html')[0].style.removeProperty('overflow')
    },
    async onScanClick() {
      await this.onDraftSave(false)
      clearInterval(this.intervalId)
      this.intervalId = null
      this.showFormTab = false
      this.showScanTab = true
    },
    checkFormulaDependencies() {
      const uuidCellsWithFormula = []
      this.activeSheet.cells.forEach((cell) => {
        if (cell.usedInCellsWithFormula?.length) {
          uuidCellsWithFormula.push(...cell.usedInCellsWithFormula)
        }
      })

      if (uuidCellsWithFormula.length) {
        this.updateCellsWithFormula(uniq(uuidCellsWithFormula))
      }
    },
    updateCellsWithFormula(cellUUIDs) {
      const uuidCellsWithFormula = []
      const updMap = new Map()
      const { all_cells_by_uuid } = this

      cellUUIDs.forEach((uuid) => {
        if (!all_cells_by_uuid[uuid]) return

        const cell = all_cells_by_uuid[uuid]
        let cellsArr = [cell]

        if (updMap.has(cell.$sheet)) {
          cellsArr = updMap.get(cell.$sheet)
          cellsArr.push(cell)
        }

        updMap.set(cell.$sheet, cellsArr)
      })

      // eslint-disable-next-line no-restricted-syntax
      for (const [sheet, updCells] of updMap) {
        Object.keys(this.dataCollection[sheet.$index]).forEach((rowIndex) => {
          updCells.forEach((cell) => {
            if (!cell.formula) return

            const formula = cell.formula?.replace(
              /\[#\$_(.+?)#\]/g,
              (match, uuid) => {
                const formulaCell = all_cells_by_uuid[uuid]
                if (!formulaCell) return 0
                let val = this.dataCollection?.[formulaCell.$sheet.$index]?.[rowIndex]?.[formulaCell.$index]
                // eslint-disable-next-line eqeqeq
                if (typeof val === 'string') {
                  val = val.replace(',', '.')
                }
                // eslint-disable-next-line eqeqeq
                return (val == +val ? val : 0)
              },
            )

            this.dataCollection[sheet.$index][rowIndex][cell.$index] = evaluateFormula(formula)

            if (cell.usedInCellsWithFormula) {
              uuidCellsWithFormula.push(...cell.usedInCellsWithFormula)
            }
          })
        })
      }

      if (uuidCellsWithFormula.length) {
        this.updateCellsWithFormula(uniq(uuidCellsWithFormula))
      }
    },
    async onShowErrors() {
      if (this.isEditDialogLoading) return
      this.isEditDialogLoading = true

      const propsData = {
        textPreparedErrors: this.textPreparedErrors,
        textPreparedBackendErrors: this.textPreparedBackendErrors,
        errors: this.errors,
        sheets: this.sheets,
      }

      const modal = await createLKPublicationsFormdataErrorsModal(this, propsData)
      await modal.open()
      this.isEditDialogLoading = false
    },
    /**
     * @returns Schedule[]
     */
    getSchedulePayload() {
      const payload = {}

      // Получаем набор дат.
      // Они находятся в колонках слева от позиции колонки с расписанием.

      this.sheets.forEach((sheet) => {
        const indices = sheet.cells
          .map((cell, idx) => (cell.type === SheetCellTypes.schedule ? idx : -1))
          .filter((idx) => idx >= 0)

        if (indices.length > 0) {
          indices.forEach((idx) => {
            sheet.$data.forEach((data) => {
              const dateRaw = data[idx - 1]
              const scheduleVal = +data[idx]
              if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateRaw)) {
                const date = dateRaw.split('.').reverse().join('-')
                payload[date] = (payload[date] ?? 0) + (Number.isNaN(scheduleVal) ? 0 : scheduleVal)
              }
            })
          })
        }
      })

      return {
        publication_id: this.publicationId,
        scheduler: Object.entries(payload).map(([date, count]) => ({ date, count })),
      }
    },
    /**
     * true, если расписание составленно корректно.
     */
    async isScheduleCorrect() {
      if (this.hasSchedule && this.publicationId) {
        const api = new LkScheduleApi()
        try {
          await api.validate(this.getSchedulePayload())
          return true
        } catch (e) {
          this.$notice.errors(e)
        }
        return false
      }
      return true
    },
  },
}
</script>

<style lang="scss">
.scan-instruction-banner {
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  margin-bottom: 1rem;
  background: var(--v-primary-lighten5, #e3f2fd);
  border: 1.5px solid var(--v-primary-base);
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, background 0.2s;

  &:hover {
    background: var(--v-primary-lighten4, #bbdefb);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  }

  &__title {
    font-weight: 600;
    font-size: 15px;
    color: var(--v-primary-darken1, #1565c0);
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 12px;
    color: var(--v-primary-base);
    margin-top: 2px;
  }
}

.LKPublicationsDataCollectsForm {
  &__empty {
    padding: 40px 20px;
    text-align: center;
  }

  &__inner {
    overflow: auto;
  }

  &__tab-title {
    display: flex;

    &--long {
      display: inline-block;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__link {
    color: $webflow-color-blue-dark-hover;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
